// Shop catalog. Add a new artwork by appending an object here — the shop,
// product pages, artist pages and cart all read from this list.
// V2: framing axis, edition sizes, curated remaining counters, release waves.
// V3: all artists back in the grid, apartment-shot covers, artist metadata
// for the educational artist pages (Paper Collective-inspired).
const ART = "https://customer-assets-jt897jd0.emergentagent.net/job_the-x-goes-here/artifacts/";

export const SIZES = [
  { id: "40x50", label: "40 × 50 cm", prices: { unframed: 75, framed: 145 } },
  { id: "50x70", label: "50 × 70 cm", prices: { unframed: 110, framed: 210 } },
  { id: "70x100", label: "70 × 100 cm", prices: { unframed: 180, framed: 360 } },
];

export const FRAMING = [{ id: "unframed" }, { id: "framed" }];

export const FRAME_COLORS = [{ id: "oak" }, { id: "black" }];

export const COLORS = [
  { id: "noir", label: "Noir" },
  { id: "bicolor", label: "Bi-color" },
];

// Edition sizes per size × colourway (Part 4 of the V2 brief).
export const EDITIONS = {
  "40x50": { noir: 100, bicolor: 25 },
  "50x70": { noir: 50, bicolor: 25 },
  "70x100": { noir: 25, bicolor: 15 },
};

// Free-shipping threshold and rates (Part 3): FR / EU, by highest tier in cart.
export const FREE_SHIPPING = 150;
export const SHIPPING = {
  tube: { FR: 12, EU: 20 }, // unframed
  framedStd: { FR: 25, EU: 45 }, // framed 40x50 / 50x70
  framedXl: { FR: 45, EU: 75 }, // framed 70x100
};

export function shippingFor(items, zone) {
  const subtotal = items.reduce((n, i) => n + i.unit_price * i.quantity, 0);
  if (subtotal >= FREE_SHIPPING) return 0;
  let tier = "tube";
  for (const i of items) {
    if (i.framing === "framed") {
      tier = i.size === "70x100" ? "framedXl" : tier === "framedXl" ? tier : "framedStd";
    }
  }
  return SHIPPING[tier][zone === "EU" ? "EU" : "FR"];
}

// Curated display counters — prints are made to order (no physical stock),
// so remaining numbers are editorial, not inventory. LOW_STOCK pins a few
// variants low to power the "Almost gone" badges.
const LOW_STOCK = {
  "basquiat:50x70:noir": 4,
  "klimt:50x70:bicolor": 3,
  "banksy:70x100:bicolor": 2,
  "warhol:40x50:bicolor": 4,
  "picasso:40x50:noir": 9,
  "kahlo:70x100:noir": 3,
};

const hash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

export function editionOf(sizeId, colorId) {
  return EDITIONS[sizeId]?.[colorId] ?? 0;
}

export function remainingFor(productId, sizeId, colorId) {
  const total = editionOf(sizeId, colorId);
  if (!total) return 0;
  const low = LOW_STOCK[`${productId}:${sizeId}:${colorId}`];
  if (low != null) return Math.min(low, total);
  // Deterministic 35–90 % of the edition.
  return Math.max(1, Math.round(total * (0.35 + (hash(productId + sizeId + colorId) % 56) / 100)));
}

export function isAlmostGone(productId, sizeId, colorId) {
  const total = editionOf(sizeId, colorId);
  const rem = remainingFor(productId, sizeId, colorId);
  return total > 0 && rem > 0 && rem / total < 0.2;
}

export function hasScarcity(productId) {
  return SIZES.some((s) =>
    COLORS.some((c) => isAlmostGone(productId, s.id, c.id))
  );
}

// Signed collector edition — 70×100 only, unframed, edition of 15,
// hand-numbered and signed, certificate of authenticity.
export const COLLECTOR = { size: "70x100", price: 390, edition: 15 };

// Made to Order — "The ______ Goes Here." One-offs, never numbered.
export const MTO_SIZES = [
  { id: "40x50", label: "40 × 50 cm", price: 110 },
  { id: "50x70", label: "50 × 70 cm", price: 150 },
  { id: "70x100", label: "70 × 100 cm", price: 220 },
];
export const MTO_FRAMED_UPLIFT = { "40x50": 70, "50x70": 100, "70x100": 180 };
export const mtoPrice = (sizeId, framingId = "unframed") => {
  const s = MTO_SIZES.find((x) => x.id === sizeId);
  if (!s) return 0;
  return s.price + (framingId === "framed" ? MTO_FRAMED_UPLIFT[sizeId] : 0);
};

// Protected names — we are the manufacturer; personalisation does not
// transfer IP liability. Mirror of the backend list (authoritative).
export const BLOCKED_WORDS = [
  "basquiat", "haring", "kaws", "murakami", "picasso", "warhol", "banksy",
  "rothko", "pollock", "lichtenstein", "hockney", "kahlo", "arsham",
  "lewitt", "newman", "miro", "miró", "matisse", "magritte", "dali", "dalí",
  "klimt", "monet",
  "porsche", "ferrari", "lamborghini", "bugatti", "bentley", "rolls-royce",
  "rolls royce", "tesla", "bmw", "mercedes", "audi", "rolex", "chanel",
  "vuitton", "louis vuitton", "hermes", "hermès", "gucci", "prada", "dior",
  "cartier", "nike", "supreme", "balenciaga", "versace", "tiffany",
];
const PROFANITY = [
  "merde", "connard", "connasse", "salope", "putain", "nazi", "hitler",
  "fuck", "shit", "bitch", "cunt", "nigger", "nigga", "whore", "slut",
];
export function checkWord(word) {
  const w = (word || "").trim().toLowerCase();
  if (!w || w.length > 24) return { allowed: false, reason: "invalid" };
  if (BLOCKED_WORDS.includes(w) || BLOCKED_WORDS.some((b) => b.length > 4 && w.includes(b)))
    return { allowed: false, reason: "protected" };
  if (PROFANITY.includes(w)) return { allowed: false, reason: "profanity" };
  return { allowed: true, reason: null };
}
export const artistMatch = (word) => {
  const w = (word || "").trim().toLowerCase();
  return PRODUCTS.find((p) => p.id === w || p.artist?.name?.toLowerCase().includes(w) && w.length > 3);
};

// Art Walls — three 40×50 unframed prints in different colourways, 195 €
// instead of 225 €. Bundle picks are product id + colourway.
export const ART_WALL_PRICE = 195;
export const ART_WALL_SETS = [
  {
    id: "mur-noir",
    image: ART + "zz68catd_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2011_16_45%20AM.png",
    picks: [
      { product: "basquiat", color: "noir" },
      { product: "picasso", color: "noir" },
      { product: "arsham", color: "noir" },
    ],
  },
  {
    id: "colour-wall",
    image: ART + "yabv1pxz_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2011_16_45%20AM.png",
    picks: [
      { product: "hockney", color: "bicolor" },
      { product: "kahlo", color: "bicolor" },
      { product: "murakami", color: "bicolor" },
    ],
  },
  {
    id: "le-salon",
    image: ART + "radzvfym_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2011_16_45%20AM.png",
    picks: [
      { product: "matisse", color: "bicolor" },
      { product: "rothko", color: "noir" },
      { product: "monet", color: "bicolor" },
    ],
  },
];

// Fulfilment promise — used in product, cart, checkout copy.
export const FULFILMENT = "10–14";

const ARTISTS = {
  basquiat: {
    name: "Jean-Michel Basquiat",
    years: "1960 — 1988",
    movement: { en: "Neo-expressionism · New York", fr: "Néo-expressionnisme · New York" },
    bio: {
      en: "From SAMO graffiti on Manhattan walls to the Whitney before thirty, Basquiat compressed street language, anatomy, jazz and Black history into canvases that still feel urgent. He painted like someone who knew time was short.",
      fr: "Des graffitis SAMO sur les murs de Manhattan à la consécration avant ses trente ans, Basquiat a compressé le langage de la rue, l'anatomie, le jazz et l'histoire afro-américaine dans des toiles toujours brûlantes d'urgence.",
    },
    works: ["Untitled (Skull), 1981", "Boy and Dog in a Johnnypump, 1982", "Hollywood Africans, 1983"],
    concept: {
      en: "The most sought-after Basquiats trade for nine figures. The cross marks the wall where one would hang — the energy of the piece is yours to imagine.",
      fr: "Les Basquiat les plus convoités s'échangent à neuf chiffres. La croix marque le mur où l'un d'eux serait accroché — l'énergie de l'œuvre vous appartient.",
    },
  },
  warhol: {
    name: "Andy Warhol",
    years: "1928 — 1987",
    movement: { en: "Pop art · New York", fr: "Pop art · New York" },
    bio: {
      en: "Warhol turned advertising, celebrity and repetition into the mirror of a century. From the Factory he industrialised the aura — and made the copy more famous than the original.",
      fr: "Warhol a fait de la publicité, de la célébrité et de la répétition le miroir d'un siècle. Depuis la Factory, il a industrialisé l'aura — et rendu la copie plus célèbre que l'original.",
    },
    works: ["Campbell's Soup Cans, 1962", "Marilyn Diptych, 1962", "Shot Marilyns, 1964"],
    concept: {
      en: "Warhol proved an image gains power by being reproduced. Here the image is absent entirely — the ultimate edition.",
      fr: "Warhol a prouvé qu'une image gagne en puissance à force d'être reproduite. Ici, l'image est carrément absente — l'édition ultime.",
    },
  },
  arsham: {
    name: "Daniel Arsham",
    years: "1980 —",
    movement: { en: "Fictional archaeology · New York", fr: "Archéologie fictionnelle · New York" },
    bio: {
      en: "Arsham casts today's objects — cars, phones, cartoons — as eroded relics of a future past. His monochrome crystalline sculptures ask what of our century will survive.",
      fr: "Arsham moule les objets d'aujourd'hui — voitures, téléphones, dessins animés — en reliques érodées d'un passé futur. Ses sculptures cristallines monochromes demandent ce qui de notre siècle survivra.",
    },
    works: ["Eroded Porsche 911, 2019", "Future Relic series, 2013–", "Blue Calcite Basketball, 2016"],
    concept: {
      en: "An Arsham is already an absence — a relic of something gone. The cross simply states it plainly.",
      fr: "Un Arsham est déjà une absence — la relique de quelque chose de disparu. La croix le dit simplement.",
    },
  },
  banksy: {
    name: "Banksy",
    years: "Active since the 1990s",
    movement: { en: "Street art · Bristol", fr: "Street art · Bristol" },
    bio: {
      en: "Anonymous, prolific and legally elusive, Banksy turned the street into the world's most visited gallery — and shredded his own painting at auction the moment it sold.",
      fr: "Anonyme, prolifique et insaisissable, Banksy a fait de la rue la galerie la plus visitée du monde — et a détruit sa propre toile aux enchères à l'instant de son adjudication.",
    },
    works: ["Girl with Balloon, 2002", "Flower Thrower, 2003", "Love is in the Bin, 2018"],
    concept: {
      en: "Banksy self-destructed a canvas in front of the art market. An empty cross on your wall is the same joke, told quietly.",
      fr: "Banksy a autodétruit une toile devant le marché de l'art. Une croix vide sur votre mur, c'est la même blague, murmurée.",
    },
  },
  dali: {
    name: "Salvador Dalí",
    years: "1904 — 1989",
    movement: { en: "Surrealism · Catalonia", fr: "Surréalisme · Catalogne" },
    bio: {
      en: "Painter of melting clocks and impossible dreams, Dalí made the subconscious a public spectacle — and himself into a work of art with a moustache.",
      fr: "Peintre des montres molles et des rêves impossibles, Dalí a fait de l'inconscient un spectacle public — et de lui-même, moustache comprise, une œuvre d'art.",
    },
    works: ["The Persistence of Memory, 1931", "Swans Reflecting Elephants, 1937", "The Elephants, 1948"],
    concept: {
      en: "Dalí painted what isn't real. The cross goes further — it hangs what isn't there.",
      fr: "Dalí peignait ce qui n'est pas réel. La croix va plus loin — elle accroche ce qui n'est pas là.",
    },
  },
  haring: {
    name: "Keith Haring",
    years: "1958 — 1990",
    movement: { en: "Street art · Pop · New York", fr: "Street art · Pop · New York" },
    bio: {
      en: "In white chalk on black subway panels, Haring invented a universal alphabet — radiant babies, barking dogs — that spoke of joy, AIDS, and justice to anyone passing by.",
      fr: "À la craie blanche sur les panneaux noirs du métro, Haring a inventé un alphabet universel — bébés irradiants, chiens qui aboient — parlant de joie, du sida et de justice à tous les passants.",
    },
    works: ["Radiant Baby, 1980s", "Crack is Wack, 1986", "Dancing Dogs, 1980s"],
    concept: {
      en: "Haring drew in the margins of the city, where ads should be. The cross hangs where the masterpiece should be.",
      fr: "Haring dessinait dans les marges de la ville, là où devraient être les publicités. La croix s'accroche là où devrait être le chef-d'œuvre.",
    },
  },
  hockney: {
    name: "David Hockney",
    years: "1937 —",
    movement: { en: "Pop · Modernism · Los Angeles", fr: "Pop · Modernisme · Los Angeles" },
    bio: {
      en: "From Yorkshire to California, Hockney has spent sixty years reinventing how we see — swimming pools, double portraits, iPad springtimes. At 88 he still paints every morning.",
      fr: "Du Yorkshire à la Californie, Hockney réinvente depuis soixante ans notre manière de voir — piscines, doubles portraits, printemps sur iPad. À 88 ans, il peint encore chaque matin.",
    },
    works: ["A Bigger Splash, 1967", "Portrait of an Artist (Pool with Two Figures), 1972", "The Arrival of Spring, 2011"],
    concept: {
      en: "The most expensive painting by a living artist hangs on somebody's wall. On yours, the cross keeps its place warm.",
      fr: "Le tableau le plus cher d'un artiste vivant est accroché sur le mur de quelqu'un. Sur le vôtre, la croix lui garde la place.",
    },
  },
  kahlo: {
    name: "Frida Kahlo",
    years: "1907 — 1954",
    movement: { en: "Magic realism · Mexico City", fr: "Réalisme magique · Mexico" },
    bio: {
      en: "Confined to her bed by a broken body, Kahlo painted herself relentlessly — pain, politics, love and Mexico woven into self-portraits that have become icons of survival.",
      fr: "Clouée au lit par un corps brisé, Kahlo s'est peinte sans relâche — douleur, politique, amour et Mexique tissés en autoportraits devenus des icônes de survie.",
    },
    works: ["The Two Fridas, 1939", "Self-Portrait with Thorn Necklace, 1940", "The Broken Column, 1944"],
    concept: {
      en: "Kahlo's house-museum keeps her bed and brushes exactly where she left them. Some presences survive best as an empty place.",
      fr: "La maison-musée de Kahlo conserve son lit et ses pinceaux exactement où elle les a laissés. Certaines présences survivent mieux comme un lieu vide.",
    },
  },
  kaws: {
    name: "KAWS — Brian Donnelly",
    years: "1974 —",
    movement: { en: "Pop · Street · Brooklyn", fr: "Pop · Street · Brooklyn" },
    bio: {
      en: "From tagging over bus-shelter ads to forty-metre inflatable Companions floating in Victoria Harbour, KAWS turned a skull-and-crossbones Mickey into a global language of melancholy play.",
      fr: "Des abribus détournés aux Companions gonflables de quarante mètres flottant dans la baie de Hong Kong, KAWS a fait d'un Mickey aux os croisés un langage mondial de mélancolie joyeuse.",
    },
    works: ["Companion, 1999–", "The KAWS Album, 2005", "BFF, 2016"],
    concept: {
      en: "KAWS built an empire on two crossed-out eyes. The X was always the signature.",
      fr: "KAWS a bâti un empire sur deux yeux barrés. Le X a toujours été la signature.",
    },
  },
  klimt: {
    name: "Gustav Klimt",
    years: "1862 — 1918",
    movement: { en: "Vienna Secession", fr: "Sécession viennoise" },
    bio: {
      en: "In gold-leafed Vienna, Klimt painted desire as ornament — lovers wrapped in Byzantine shimmer. Stolen, burned and recovered, his works carry a century of obsession.",
      fr: "Dans la Vienne de la feuille d'or, Klimt a peint le désir en ornement — des amants enveloppés de scintillement byzantin. Volées, brûlées, retrouvées : ses œuvres portent un siècle d'obsession.",
    },
    works: ["The Kiss, 1907–08", "Portrait of Adele Bloch-Bauer I, 1907", "The Tree of Life, 1909"],
    concept: {
      en: "The missing Klimt is a true story — several vanished in the war and were never found. The cross marks their wall.",
      fr: "Le Klimt manquant est une histoire vraie — plusieurs ont disparu pendant la guerre et n'ont jamais été retrouvés. La croix marque leur mur.",
    },
  },
  lewitt: {
    name: "Sol LeWitt",
    years: "1928 — 2007",
    movement: { en: "Conceptual art · Minimalism", fr: "Art conceptuel · Minimalisme" },
    bio: {
      en: "LeWitt's wall drawings exist first as instructions — anyone can execute them, the idea is the artwork. He gave the art world a radical gift: the certificate, not the wall.",
      fr: "Les wall drawings de LeWitt existent d'abord comme des instructions — n'importe qui peut les exécuter, l'idée est l'œuvre. Il a offert au monde de l'art un cadeau radical : le certificat, pas le mur.",
    },
    works: ["Wall Drawings, 1968–2007", "Incomplete Open Cubes, 1974", "Bands of Color, 1990s"],
    concept: {
      en: "LeWitt proved a wall work can be a set of instructions. Ours is one line: paint a cross where it should hang.",
      fr: "LeWitt a prouvé qu'une œuvre murale peut être une simple instruction. La nôtre tient en une ligne : peignez une croix à l'endroit où elle devrait être accrochée.",
    },
  },
  lichtenstein: {
    name: "Roy Lichtenstein",
    years: "1923 — 1997",
    movement: { en: "Pop art · New York", fr: "Pop art · New York" },
    bio: {
      en: "With Ben-Day dots and comic-strip panels blown to museum size, Lichtenstein asked what separates cheap print from high art — and charged millions for the answer.",
      fr: "Avec ses trames Ben-Day et ses cases de BD agrandies à l'échelle des musées, Lichtenstein demandait ce qui sépare l'imprimé bon marché du grand art — et facturait la réponse des millions.",
    },
    works: ["Look Mickey, 1961", "Whaam!, 1963", "Drowning Girl, 1963"],
    concept: {
      en: "Lichtenstein made prints that pretend to be paintings of prints. The cross is a print of the idea of a painting.",
      fr: "Lichtenstein faisait des estampes faisant semblant d'être des peintures d'estampes. La croix est l'impression de l'idée d'un tableau.",
    },
  },
  magritte: {
    name: "René Magritte",
    years: "1898 — 1967",
    movement: { en: "Surrealism · Brussels", fr: "Surréalisme · Bruxelles" },
    bio: {
      en: "A man in a bowler hat, painting pipes that are not pipes. Magritte's calm, exact images quietly sabotage the difference between a thing and its picture.",
      fr: "Un homme au chapeau melon, peignant des pipes qui ne sont pas des pipes. Les images calmes et précises de Magritte sabotent en douceur la différence entre une chose et son image.",
    },
    works: ["The Treachery of Images, 1929", "The Empire of Light, 1953–54", "The Son of Man, 1964"],
    concept: {
      en: "Ceci n'est pas une pipe. The cross follows Magritte's logic to its end: this is not a Picasso, and it says so.",
      fr: "Ceci n'est pas une pipe. La croix pousse la logique de Magritte jusqu'au bout : ceci n'est pas un Picasso, et c'est écrit dessus.",
    },
  },
  matisse: {
    name: "Henri Matisse",
    years: "1869 — 1954",
    movement: { en: "Fauvism · Nice", fr: "Fauvisme · Nice" },
    bio: {
      en: "The wild beast of colour spent his last decade bedridden, cutting painted paper with scissors — 'drawing with colour' — and produced his freest work when painting was impossible.",
      fr: "Le fauve de la couleur a passé sa dernière décennie alité, découpant au ciseau du papier peint — « dessiner avec la couleur » — et a produit son œuvre la plus libre quand peindre était impossible.",
    },
    works: ["The Dance, 1910", "The Red Studio, 1911", "Blue Nudes, 1952"],
    concept: {
      en: "Matisse said a drawing is an empty space that learns to breathe. The cross is exactly that — air with an intention.",
      fr: "Matisse disait qu'un dessin est un espace vide qui apprend à respirer. La croix est exactement cela — de l'air avec une intention.",
    },
  },
  miro: {
    name: "Joan Miró",
    years: "1893 — 1983",
    movement: { en: "Surrealism · Catalonia", fr: "Surréalisme · Catalogne" },
    bio: {
      en: "Miró spent a lifetime 'assassinating painting' with stars, birds and women drawn like constellations — a Catalan grammar of joy that never repeated itself.",
      fr: "Miró a passé sa vie à « assassiner la peinture » avec des étoiles, des oiseaux et des femmes dessinés comme des constellations — une grammaire catalane de la joie qui ne s'est jamais répétée.",
    },
    works: ["The Farm, 1921–22", "Constellations, 1940–41", "Woman and Bird, 1982"],
    concept: {
      en: "Miró erased the painting to save it. A cross where the canvas should be would not have shocked him.",
      fr: "Miró effaçait la peinture pour la sauver. Une croix à la place de la toile ne l'aurait pas choqué.",
    },
  },
  monet: {
    name: "Claude Monet",
    years: "1840 — 1926",
    movement: { en: "Impressionism · Giverny", fr: "Impressionnisme · Giverny" },
    bio: {
      en: "Half-blind in his garden at Giverny, Monet painted the same water lilies hundreds of times, chasing a light that never held still. Impressionism began with one of his titles.",
      fr: "À moitié aveugle dans son jardin de Giverny, Monet a peint les mêmes nymphéas des centaines de fois, poursuivant une lumière jamais fixe. L'impressionnisme est né d'un de ses titres.",
    },
    works: ["Impression, Sunrise, 1872", "Water Lilies, 1897–1926", "Rouen Cathedral, 1892–94"],
    concept: {
      en: "Monet painted light, not objects. The cross paints intention, not image — the purest impression of all.",
      fr: "Monet peignait la lumière, pas les objets. La croix peint l'intention, pas l'image — la plus pure des impressions.",
    },
  },
  murakami: {
    name: "Takashi Murakami",
    years: "1962 —",
    movement: { en: "Superflat · Tokyo", fr: "Superflat · Tokyo" },
    bio: {
      en: "Trained in classical Nihonga, Murakami flattened high art and otaku culture into one surface — smiling flowers, skulls and Louis Vuitton monograms, all equally serious.",
      fr: "Formé au Nihonga classique, Murakami a aplati grand art et culture otaku sur une même surface — fleurs souriantes, crânes et monogrammes Vuitton, tous également sérieux.",
    },
    works: ["727, 1996", "Flower Ball, 2002", "My Lonesome Cowboy, 1998"],
    concept: {
      en: "Superflat erased the hierarchy between souvenir and masterpiece. An empty cross is its final form — content removed, aura intact.",
      fr: "Le Superflat a effacé la hiérarchie entre souvenir et chef-d'œuvre. Une croix vide en est la forme finale — le contenu retiré, l'aura intacte.",
    },
  },
  newman: {
    name: "Barnett Newman",
    years: "1905 — 1970",
    movement: { en: "Abstract expressionism · New York", fr: "Expressionnisme abstrait · New York" },
    bio: {
      en: "Newman's vast monochrome fields, split by a single vertical 'zip', asked the viewer to stand close and feel addressed. Who's Afraid of Red, Yellow and Blue was attacked twice — proof it worked.",
      fr: "Les vastes champs monochromes de Newman, fendus d'un unique « zip » vertical, demandent au spectateur de s'approcher et de se sentir interpellé. Who's Afraid of Red, Yellow and Blue a été vandalisé deux fois — la preuve que ça marche.",
    },
    works: ["Vir Heroicus Sublimis, 1950–51", "Stations of the Cross, 1958–66", "Broken Obelisk, 1963"],
    concept: {
      en: "Newman made one line carry an entire canvas. The X makes two strokes carry an entire wall.",
      fr: "Newman faisait porter une toile entière à une seule ligne. Le X fait porter un mur entier à deux traits.",
    },
  },
  picasso: {
    name: "Pablo Picasso",
    years: "1881 — 1973",
    movement: { en: "Cubism · Málaga, Paris", fr: "Cubisme · Málaga, Paris" },
    bio: {
      en: "Seventy-eight years of painting, fifty thousand works, a century reinvented several times over. Picasso didn't have a style — he had appetites.",
      fr: "Soixante-dix-huit ans de peinture, cinquante mille œuvres, un siècle réinventé plusieurs fois. Picasso n'avait pas un style — il avait des appétits.",
    },
    works: ["Les Demoiselles d'Avignon, 1907", "Guernica, 1937", "The Weeping Woman, 1937"],
    concept: {
      en: "'It took me four years to paint like Raphael, but a lifetime to paint like a child.' The cross is the childhood drawing of an acquisition.",
      fr: "« J'ai mis quatre ans à peindre comme Raphaël, une vie à peindre comme un enfant. » La croix est le dessin d'enfant d'une acquisition.",
    },
  },
  pollock: {
    name: "Jackson Pollock",
    years: "1912 — 1956",
    movement: { en: "Abstract expressionism · New York", fr: "Expressionnisme abstrait · New York" },
    bio: {
      en: "Canvas on the floor, paint in the air: Pollock's drips recorded the dance of making them. 'I am nature,' he said, when asked why he didn't paint it.",
      fr: "La toile au sol, la peinture dans l'air : les drippings de Pollock enregistrent la danse de leur fabrication. « Je suis la nature », répondait-il à qui lui demandait pourquoi il ne la peignait pas.",
    },
    works: ["Number 1A, 1948", "Autumn Rhythm, 1950", "Blue Poles, 1952"],
    concept: {
      en: "Pollock's gesture was the artwork. Two strokes of a cross is the most economical gesture ever hung.",
      fr: "Le geste de Pollock était l'œuvre. Deux traits de croix forment le geste le plus économe jamais accroché.",
    },
  },
  rothko: {
    name: "Mark Rothko",
    years: "1903 — 1970",
    movement: { en: "Color field · New York", fr: "Color field · New York" },
    bio: {
      en: "Rothko wanted his glowing rectangles to make viewers weep — 'basic human emotions: tragedy, ecstasy, doom.' He asked that they be hung low, in dim rooms, close enough to be entered.",
      fr: "Rothko voulait que ses rectangles lumineux fassent pleurer — « les émotions humaines fondamentales : la tragédie, l'extase, le destin ». Il demandait qu'on les accroche bas, dans des salles tamisées, assez près pour y entrer.",
    },
    works: ["No. 61 (Rust and Blue), 1953", "Seagram Murals, 1958", "Rothko Chapel, 1964–67"],
    concept: {
      en: "Rothko proved colour alone can hold a room. The cross proves a sentence can.",
      fr: "Rothko a prouvé que la couleur seule peut tenir une pièce. La croix prouve qu'une phrase le peut aussi.",
    },
  },
};

export const PRODUCTS = [
  {
    id: "basquiat",
    name: "The Basquiat",
    tagline: "The Basquiat goes here.",
    artist: ARTISTS.basquiat,
    cover: ART + "itg58qw2_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2009_51_55%20AM.png",
    card: "https://static.prod-images.emergentagent.com/jobs/0e811c03-4ca3-468f-b8e6-122181414b6d/images/1ac1dd8c1093e77db251006caa90c7738fd189fcd91e34f2088f6135f9c08738.jpeg",
    prints: {
      noir: ART + "3t4di3be_The%20Basquiat%20Goes%20Here-01.webp",
      bicolor: ART + "pjj9nxvd_The%20Basquiat%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "itg58qw2_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2009_51_55%20AM.png",
      ART + "radzvfym_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2011_16_45%20AM.png",
    ],
  },
  {
    id: "warhol",
    name: "The Warhol",
    tagline: "The Warhol goes here.",
    artist: ARTISTS.warhol,
    card: "https://static.prod-images.emergentagent.com/jobs/0e811c03-4ca3-468f-b8e6-122181414b6d/images/35003e4b6be1c4fb6bd7e894d61ad136ee572a9f0432c9ef30b19c682eb452b2.jpeg",
    prints: {
      noir: ART + "4s13mxdd_Screenshot%202026-09-01%20at%2016.09.36.png",
      bicolor: ART + "ur1olbnm_Screenshot%202026-09-01%20at%2016.09.27.png",
    },
    gallery: [
      ART + "gmtjzfze_Screenshot%202026-09-01%20at%2016.07.42.png",
    ],
  },
  {
    id: "arsham",
    name: "The Arsham",
    tagline: "The Arsham goes here.",
    artist: ARTISTS.arsham,
    cardColor: "noir",
    cover: ART + "2u93pkcx_ChatGPT%20Image%20Sep%206%2C%202026%2C%2011_32_39%20AM.png",
    card: "https://static.prod-images.emergentagent.com/jobs/0e811c03-4ca3-468f-b8e6-122181414b6d/images/975e8abe87121854055b3858b4438e987a0ec398348135f125dc99621b7ffcae.jpeg",
    prints: {
      noir: ART + "k3utv586_The%20Arsham%20Goes%20Here-01.webp",
      bicolor: ART + "geo0wb3u_The%20Arsham%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "2u93pkcx_ChatGPT%20Image%20Sep%206%2C%202026%2C%2011_32_39%20AM.png",
      ART + "fsrnigtx_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2011_28_06%20AM.png",
      ART + "jpvumqgf_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2011_29_32%20AM.png",
    ],
  },
  {
    id: "banksy",
    name: "The Banksy",
    tagline: "The Banksy goes here.",
    artist: ARTISTS.banksy,
    cover: ART + "anaj0dyf_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2011_53_31%20AM.png",
    prints: {
      noir: ART + "8csu8exo_The%20Banksy%20Goes%20Here-01.jpg.webp",
      bicolor: ART + "6tew0984_The%20Banksy%20Goes%20Here-02.jpg.webp",
    },
    gallery: [
      ART + "anaj0dyf_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2011_53_31%20AM.png",
      ART + "ztxpjrly_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2011_32_21%20AM.png",
      ART + "ez497v07_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2011_31_03%20AM.png",
      ART + "fejczhs1_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2011_53_31%20AM.png",
    ],
  },
  {
    id: "dali",
    name: "The Dalí",
    tagline: "The Dalí goes here.",
    artist: ARTISTS.dali,
    prints: {
      noir: ART + "9oy43oc8_The%20Dali%20Goes%20Here-01.webp",
      bicolor: ART + "eorsykwc_The%20Dali%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "3ael48fr_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2011_59_50%20AM.png",
    ],
  },
  {
    id: "haring",
    name: "The Haring",
    tagline: "The Haring goes here.",
    artist: ARTISTS.haring,
    prints: {
      noir: ART + "m2ux8pia_The%20Haring%20Goes%20Here-01.webp",
      bicolor: ART + "xqrwyf03_The%20Haring%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "kd7xvbv1_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2012_04_11%20PM.png",
    ],
  },
  {
    id: "hockney",
    name: "The Hockney",
    tagline: "The Hockney goes here.",
    artist: ARTISTS.hockney,
    prints: {
      noir: ART + "1dojm4ed_The%20Hockney%20Goes%20Here-01.webp",
      bicolor: ART + "n4rpb1qt_The%20Hockney%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "5rn6rkhy_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2002_04_25%20PM.png",
    ],
  },
  {
    id: "kahlo",
    name: "The Kahlo",
    tagline: "The Kahlo goes here.",
    artist: ARTISTS.kahlo,
    prints: {
      noir: ART + "ml4f67e4_The%20Kahlo%20Goes%20Here-01.webp",
      bicolor: ART + "mzgium8e_The%20Kahlo%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "7vjz9902_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2002_08_36%20PM.png",
    ],
  },
  {
    id: "kaws",
    name: "The Kaws",
    tagline: "The Kaws goes here.",
    artist: ARTISTS.kaws,
    prints: {
      noir: ART + "jf8d5677_The%20Kaws%20Goes%20Here-01.webp",
      bicolor: ART + "qta2rfy8_The%20Kaws%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "8e46oscp_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2009_59_15%20AM.png",
    ],
  },
  {
    id: "klimt",
    name: "The Klimt",
    tagline: "The Klimt goes here.",
    artist: ARTISTS.klimt,
    prints: {
      noir: ART + "7svue4cg_The%20Klimt%20Goes%20Here-01.webp",
      bicolor: ART + "x9f1aboy_The%20Klimt%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "8cmni5v8_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2002_43_42%20PM.png",
    ],
  },
  {
    id: "lewitt",
    name: "The LeWitt",
    tagline: "The LeWitt goes here.",
    artist: ARTISTS.lewitt,
    prints: {
      noir: ART + "3pv8eyd4_The%20LeWitt%20Goes%20Here-01.webp",
      bicolor: ART + "zre9gkhf_The%20LeWitt%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "5tnhaxl6_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2002_46_48%20PM.png",
    ],
  },
  {
    id: "lichtenstein",
    name: "The Lichtenstein",
    tagline: "The Lichtenstein goes here.",
    artist: ARTISTS.lichtenstein,
    prints: {
      noir: ART + "jt5xavr0_The%20Lichtenstein%20Goes%20Here-01.webp",
      bicolor: ART + "5yetlisu_The%20Lichtenstein%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "eybm2nqa_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2002_49_30%20PM.png",
    ],
  },
  {
    id: "magritte",
    name: "The Magritte",
    tagline: "The Magritte goes here.",
    artist: ARTISTS.magritte,
    prints: {
      noir: ART + "fah98oe3_The%20Magritte%20Goes%20Here-01.webp",
      bicolor: ART + "afpr2xif_The%20Magritte%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "6nvuzix8_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2002_54_25%20PM.png",
    ],
  },
  {
    id: "matisse",
    name: "The Matisse",
    tagline: "The Matisse goes here.",
    artist: ARTISTS.matisse,
    prints: {
      noir: ART + "j76fc3kc_The%20Matisse%20Goes%20Here-01.webp",
      bicolor: ART + "2a9agqvt_The%20Matisse%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "lvbimzf6_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2002_56_46%20PM.png",
    ],
  },
  {
    id: "miro",
    name: "The Miró",
    tagline: "The Miró goes here.",
    artist: ARTISTS.miro,
    prints: {
      noir: ART + "cev0kfqj_The%20Miro%20Goes%20Here-01.webp",
      bicolor: ART + "a96o748u_The%20Miro%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "3wca0id8_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2003_08_06%20PM.png",
    ],
  },
  {
    id: "monet",
    name: "The Monet",
    tagline: "The Monet goes here.",
    artist: ARTISTS.monet,
    prints: {
      noir: ART + "u0lww655_The%20Monet%20Goes%20Here-01.webp",
      bicolor: ART + "3gv2fif8_The%20Monet%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "6gwmexfv_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2003_33_22%20PM.png",
    ],
  },
  {
    id: "murakami",
    name: "The Murakami",
    tagline: "The Murakami goes here.",
    artist: ARTISTS.murakami,
    prints: {
      noir: ART + "ekxm998b_The%20Murakami%20Goes%20Here-01.webp",
      bicolor: ART + "8dwpun6m_The%20Murakami%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "i706x1tx_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2010_04_43%20AM.png",
    ],
  },
  {
    id: "newman",
    name: "The Newman",
    tagline: "The Newman goes here.",
    artist: ARTISTS.newman,
    prints: {
      noir: ART + "0ntal4dz_The%20Newman%20Goes%20Here-01.webp",
      bicolor: ART + "wnptehf9_The%20Newman%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "ljxjfhso_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2003_40_10%20PM.png",
      ART + "zz68catd_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2011_16_45%20AM.png",
    ],
  },
  {
    id: "picasso",
    name: "The Picasso",
    tagline: "The Picasso goes here.",
    artist: ARTISTS.picasso,
    cardColor: "noir",
    prints: {
      noir: ART + "k5h3aryd_The%20Picasso%20Goes%20Here-01.jpg.webp",
      bicolor: ART + "faqjijvh_The%20Picasso%20Goes%20Here-02.jpg.webp",
    },
    gallery: [
      ART + "k8rex21y_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2003_51_03%20PM.png",
      ART + "7i6l1tpc_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2011_16_45%20AM.png",
      ART + "1clnft4w_ChatGPT%20Image%20Aug%2028%2C%202026%2C%2005_12_36%20PM.png",
    ],
  },
  {
    id: "pollock",
    name: "The Pollock",
    tagline: "The Pollock goes here.",
    artist: ARTISTS.pollock,
    prints: {
      noir: ART + "mlpbpos4_The%20Pollock%20Goes%20Here-01.webp",
      bicolor: ART + "sixhpibr_The%20Pollock%20Goes%20Here-02.webp",
    },
    gallery: [
      ART + "k4ciwm6e_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2003_53_35%20PM.png",
      ART + "yabv1pxz_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2011_16_45%20AM.png",
    ],
  },
  {
    id: "rothko",
    name: "The Rothko",
    tagline: "The Rothko goes here.",
    artist: ARTISTS.rothko,
    card: "https://static.prod-images.emergentagent.com/jobs/0e811c03-4ca3-468f-b8e6-122181414b6d/images/42d6b0fb0326b483316c42102798023ba840a70b499a5b30486f46ffdd20c98c.jpeg",
    prints: {
      noir: ART + "djnc8j0v_Screenshot%202026-09-01%20at%2016.51.01.png",
      bicolor: ART + "p85emsnj_Screenshot%202026-09-01%20at%2016.51.08.png",
    },
    gallery: [
      ART + "5b75d3gq_Screenshot%202026-09-01%20at%2016.51.27.png",
    ],
  },
];

// Every product is live in the grid (V3 — no curation waves for now).
export const LAUNCH_PRODUCTS = PRODUCTS;

export const getProduct = (id) => PRODUCTS.find((p) => p.id === id);

// Cover for grids: the in-situ apartment shot (user rule), falling back to print.
export const coverOf = (p) =>
  p.cover || (p.gallery && p.gallery[0]) || p.prints[p.cardColor || "bicolor"];

export const priceFor = (sizeId, framingId = "unframed") =>
  SIZES.find((s) => s.id === sizeId)?.prices[framingId] ?? 0;

export const priceForSize = (sizeId) => priceFor(sizeId, "unframed");

export const eur = (n, lang = "en") =>
  new Intl.NumberFormat(lang === "fr" ? "fr-FR" : "en-IE", {
    style: "currency",
    currency: "EUR",
  }).format(n);
